'use client'

import classNames from 'clsx'
import dynamic from 'next/dynamic'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Headroom from 'react-headroom'

import type { ModelsUserFullInfoResponse } from '@/api/generated-api'
import Arena from '@/assets/icons/arena.svg'
import BurgerIcon from '@/assets/icons/burger.svg'
import { URLS } from '@/configs/urls'
import { NotificationBell } from '@/features/notifications/components/NotificationBell'
import { BellIcon } from '@/features/notifications/components/NotificationBell/BellIcon'
import { useNotificationsUserId } from '@/features/notifications/hooks/useNotificationsUserId'
import { isClubUserSession } from '@/features/tasks/fanTasksAccess'
import { useHeaderMenuItems } from '@/hooks/useHeaderMenuItems'
import { Link, usePathname } from '@/i18n/routing'
import { useHydratedAuthUi } from '@/lib/react/useHydratedAuthUi'
import { useClientProvidersReady } from '@/providers/ClientProvidersReadyContext'
import LangSelector from '@/ui/LangSelector'

import S from './Header.module.scss'
import HeaderAuthProbe from './HeaderAuthProbe'
import HeaderConnectSlot from './HeaderConnectSlot'
import JgglLink from './JgglLink'

const GuestHeaderDrawer = dynamic(() => import('./GuestHeaderDrawer'), {
  ssr: false
})

type HeaderProps = {
  initialHasSession?: boolean
}

export default function Header({ initialHasSession = false }: HeaderProps) {
  const t = useTranslations('header')
  const menuItems = useHeaderMenuItems()
  const pathname = usePathname()
  const providersReady = useClientProvidersReady()
  const hasHydratedAuthUi = useHydratedAuthUi()
  const notificationsUserId = useNotificationsUserId()
  // C9: the notification centre is a fan's; clubs have none in the MVP.
  const isClubSession = useMemo(
    () => Boolean(notificationsUserId) && isClubUserSession(),
    [notificationsUserId]
  )
  const showBell = Boolean(notificationsUserId) && !isClubSession
  const [authState, setAuthState] = useState<{
    user: ModelsUserFullInfoResponse | undefined
    hasToken: boolean
  } | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [portalReady, setPortalReady] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const guestDrawerRootRef = useRef<HTMLDivElement>(null)
  const isProfileEditPage = pathname?.includes('/profile/edit')
  // Tasks hero glow runs under the header; the glass blur turned it into a band.
  const isTransparentAtTop = pathname === URLS.TASKS
  const handleAuthState = useCallback(
    (state: {
      user: ModelsUserFullInfoResponse | undefined
      hasToken: boolean
    }) => {
      setAuthState(state)
    },
    []
  )

  const user = providersReady ? authState?.user : undefined
  const hasToken = providersReady
    ? authState?.hasToken ?? false
    : initialHasSession

  useEffect(() => {
    queueMicrotask(() => {
      setPortalReady(true)
    })
  }, [])

  // The inline script of the layout sets `data-session` from the cookies so
  // the bell slot is reserved before hydration (FE-28); from here on the
  // client knows better — e.g. after a logout without a reload.
  useEffect(() => {
    if (!hasHydratedAuthUi) {
      return
    }
    document.documentElement.toggleAttribute('data-session', showBell)
  }, [hasHydratedAuthUi, showBell])

  useEffect(() => {
    if (!isMenuOpen) {
      return
    }
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [isMenuOpen])

  useEffect(() => {
    if (!isMenuOpen) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target)) {
        return
      }
      if (guestDrawerRootRef.current?.contains(target)) {
        return
      }
      setIsMenuOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isMenuOpen])

  useEffect(() => {
    if (!isMenuOpen) {
      return
    }
    const handleScroll = () => setIsMenuOpen(false)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isMenuOpen])

  const closeGuestDrawer = () => setIsMenuOpen(false)

  const showGuestBurger = hasHydratedAuthUi
    ? !user && !hasToken
    : !initialHasSession

  if (pathname === URLS.JOIN || pathname === '/maintenance') {
    return null
  }

  const guestDrawer =
    portalReady &&
    isMenuOpen &&
    typeof document !== 'undefined' &&
    createPortal(
      <GuestHeaderDrawer
        drawerRootRef={guestDrawerRootRef}
        onClose={closeGuestDrawer}
        isProfileEditPage={isProfileEditPage}
        pathname={pathname}
        menuItems={menuItems}
        t={t}
      />,
      document.body
    )

  const header = (
    <header
      className={classNames(
        S.header,
        isTransparentAtTop && S.headerTransparentAtTop
      )}
    >
      <div className={S.logoContainer}>
        <Link href={URLS.MAIN} className={S.logo} aria-label="Arena home">
          <Arena className={S.logoImage} />
        </Link>
      </div>
      <div className={S.menuContainer}>
        <nav className={S.menu}>
          {menuItems.map(item => {
            const isActive =
              item.href === '/'
                ? pathname === '/'
                : pathname?.startsWith(item.href)
            return (
              <Link
                key={item.href}
                href={item.href}
                prefetch={
                  item.href.includes('/leaderboard') ? false : undefined
                }
                className={classNames(
                  S.menuItem,
                  isActive && S.menuItemActive,
                  item.disabled && S.menuItemDisabled
                )}
                style={{
                  opacity: item.disabled ? 0.2 : isActive ? 1 : 0.6
                }}
                onClick={e => {
                  if (item.disabled) {
                    e.preventDefault()
                  }
                }}
              >
                {item.label}
              </Link>
            )
          })}
          <JgglLink />
        </nav>
      </div>
      <div className={S.rightSection} ref={menuRef}>
        <span className={classNames(S.bellSlot, showBell && S.bellSlotActive)}>
          {showBell ? (
            <NotificationBell key={notificationsUserId} />
          ) : notificationsUserId ? null : (
            // Looks like the bell until the app hydrates; a click on it is
            // remembered by the inline script and replayed by the bell.
            <span
              className={S.bellPlaceholder}
              data-bell-placeholder=""
              aria-hidden="true"
            >
              <BellIcon />
            </span>
          )}
        </span>
        {!isProfileEditPage && (
          <div className={S.langDesktop}>
            <LangSelector />
          </div>
        )}
        <HeaderConnectSlot />
        {providersReady ? <HeaderAuthProbe onState={handleAuthState} /> : null}
        {showGuestBurger ? (
          <button
            type="button"
            className={classNames(
              S.burgerButton,
              isMenuOpen && S.burgerButtonActive
            )}
            aria-label={t('ariaLabels.menu')}
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(prev => !prev)}
          >
            <BurgerIcon className={S.burgerIcon} />
          </button>
        ) : null}
      </div>
    </header>
  )

  return (
    <>
      <Headroom className={S.headroom} disable={isMenuOpen}>
        {header}
      </Headroom>
      {guestDrawer}
    </>
  )
}
